import React, { useState, useEffect } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Table,
  Spinner,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getTechniciansList } from '../ManageServiceOrders/actions/action';
import Select from 'react-select';
import { createTO, searchServiceItemSKU } from './actions/action';
import toast from 'react-hot-toast';

const initialFormState = {
  country: 'ae',
  technicianId: '',
  technicianName: '',
  remark: '',
  items: [], // { id, sku, title, qty }
  transferFromCode: 'TP-DCWMS',
  transferToCode: 'PURF-TECH',
  transferType: "1",
  createdBy: "",
};

const CreateTOOffcanvas = ({ isOpen, toggle, editData }) => {
      const users = JSON.parse(localStorage?.getItem('authUser'));
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialFormState);
  const [skuSearch, setSkuSearch] = useState('');
  const [skuResults, setSkuResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  

  //fetch technicians list
  useEffect(() => {
    getTechniciansList({ dispatch });
  }, []);
  
  const { techniciansList } = useSelector((state) => state.technician);

  useEffect(() => {
    if (isOpen) {
      if (editData && editData.isFromRequest) {
        setForm({
          ...initialFormState,
          items: editData.items.map((item, index) => ({
            id: `request-${index}-${Date.now()}`,
            sku: item.sku,
            title: item.title || item.sku,
            quantity: item.requested_qty || item.qty || 1
          })),
          technicianId: editData.technicianId || editData.assign_staff_id || editData.staff_id || '',
          remark: `Created Staff: ${editData.staff_id}`
        });
      } else if (editData && !editData.isFromRequest) {
          // Normal edit mode (if implemented)
          setForm({ ...initialFormState, ...editData });
      } else {
        setForm(initialFormState);
      }
      setSkuSearch('');
      setSkuResults([]);
    }
  }, [isOpen, editData, dispatch]);

  const addItemToList = (item) => {
    const existingItem = form.items.find(i => i.id === item.id);
    if (existingItem) {
      const newItems = form.items.map(i => 
        i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
      );
      setForm({ ...form, items: newItems });
    } else {
      setForm({
        ...form,
        items: [...form.items, { id: item.id, sku: item.sku, title: item.title, quantity: 1 }]
      });
    }
    setSkuSearch('');
    setSkuResults([]);
  };

  const addManualItem = (sku) => {
    const timestamp = Date.now();
    const newItem = {
      id: `manual-${timestamp}`,
      sku: sku,
      title: sku, // Default title to SKU
      quantity: 1,
      isManual: true
    };
    
    setForm({
      ...form,
      items: [...form.items, newItem]
    });
    setSkuSearch('');
    setSkuResults([]);
  };

  const handleTitleChange = (id, newTitle) => {
    const newItems = form.items.map(i => 
      i.id === id ? { ...i, title: newTitle } : i
    );
    setForm({ ...form, items: newItems });
  };

  const handleQtyChange = (id, newQty) => {
    const newItems = form.items.map(i => 
      i.id === id ? { ...i, quantity: parseInt(newQty) || 0 } : i
    );
    setForm({ ...form, items: newItems });
  };

  const removeItem = (id) => {
    setForm({ ...form, items: form.items.filter(i => i.id !== id) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if(form.items.length === 0){
      toast.error("Please add at least one item");
      return;
    }else if (!form.technicianId) {
      toast.error("Please select a technician");
      return;
    }
    handleSaveItem(form);
  };


  const handleSaveItem = async (data) => {
        setIsLoading(true);
        data.createdBy = users?.salesPersonCode;
        if (editData?.isFromRequest) {
          data.type = 'staffRequest';
        }
        try {
            const response = await createTO({ data, dispatch });
            
            if (response?.code === 100) {
                toast.success('TO created successfully!');
                toggle();
                setIsLoading(false);
            } else {
                toast.error(response?.message || 'Failed to create TO');
                setIsLoading(false);
            }
        } catch (error) {
            console.error("Error creating TO:", error);
            toast.error('Something went wrong!');
            setIsLoading(false);
        }
    };

  const handleSearchSKU = async (e) => {
    const value = e.target.value;
    setSkuSearch(value);
    if (value.length > 2) {
      setIsSearching(true);
      try {
        const res = await searchServiceItemSKU({ dispatch, params: { sku: value } });
        // Action now returns response.data directly
        if (Array.isArray(res)) {
          setSkuResults(res);
        } else {
          setSkuResults([]);
        }
      } catch (error) {
        console.error("Search failed:", error);
        setSkuResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSkuResults([]);
    }
  };

  const technicianOptions = techniciansList?.technicians?.map(tech => ({
    value: tech.emp_id,
    label: tech.technician_name ? `${tech.technician_name} (${tech.email})` : tech.email,
    technicianName: tech.technician_name || ''
  })) || [];

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '600px' }}
      backdrop="static"
    >
      <OffcanvasHeader>
        Create Transfer Order (TO)
      </OffcanvasHeader>
      <OffcanvasBody>
        <Form onSubmit={handleSubmit}>
          {!editData?.isFromRequest && (
            <FormGroup className="position-relative">
              <Label for="skuSearch">Search SKU</Label>
              <Input
                type="text"
                id="skuSearch"
                placeholder="Search SKU..."
                value={skuSearch}
                onChange={(e) => handleSearchSKU(e)}
                autoComplete="off"
              />
              {isSearching && (
                <div className="position-absolute end-0 top-50 translate-middle-y me-2 mt-2">
                  <Spinner size="sm" color="primary" />
                </div>
              )}
              
              {(skuResults.length > 0 || (skuSearch.length > 0 && !isSearching)) && (
                <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                  {skuResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="list-group-item list-group-item-action py-2"
                      style={{ backgroundColor: '#f0fff4' }}
                      onClick={() => addItemToList(item)}
                    >
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">{item.sku}</span>
                        <small className="text-muted">{item.product_type}</small>
                      </div>
                      <div className="small text-truncate">{item.title}</div>
                    </button>
                  ))}
                </div>
              )}
            </FormGroup>
          )}

          <div className="mt-2">
            <h6 className="border-bottom pb-2">Selected Items</h6>
            <Table size="sm" bordered responsive>
              <thead className="table-light">
                <tr>
                  <th>SKU</th>
                  <th>Title</th>
                  <th style={{ width: '80px' }}>Qty</th>
                  {!editData?.isFromRequest && (
                    <th style={{ width: '50px' }}></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {form.items.length > 0 ? (
                  form.items.map((item) => (
                    <tr key={item.id}>
                      <td className="align-middle small fw-bold text-nowrap">{item.sku}</td>
                      <td className="align-middle">
                        {item.isManual ? (
                          <Input
                            type="text"
                            size="sm"
                            value={item.title}
                            placeholder="Enter item title..."
                            onChange={(e) => handleTitleChange(item.id, e.target.value)}
                            className="py-0"
                          />
                        ) : (
                          <span className="small">{item.title}</span>
                        )}
                      </td>
                      <td>
                        <Input
                          type="number"
                          size="sm"
                          value={item.quantity}
                          min="1"
                          onChange={(e) => handleQtyChange(item.id, e.target.value)}
                          disabled={!!editData?.isFromRequest}
                        />
                      </td>
                      {!editData?.isFromRequest && (
                        <td className="text-center">
                          <Button
                            color="link"
                            className="text-danger p-0"
                            onClick={() => removeItem(item.id)}
                        >
                          <i className="fa fa-times-circle"></i>
                        </Button>
                      </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-3">No items added</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <Row>
          <FormGroup className="col-md-6 mt-4">
            <Label for="transferFromCode">Transfer From</Label>
            <Input
              type="text"
              id="transferFromCode"
              value={"TP-DCWMS"}
              readOnly
              disabled
            />
          </FormGroup>

          <FormGroup className="col-md-6 mt-4">
            <Label for="transferToCode">Transfer To</Label>
            <Input
              type="text"
              id="transferToCode"
              value={"PURF-TECH"}
              readOnly
              disabled
            />
          </FormGroup>
          </Row>

          <FormGroup className="mt-4">
            <Label for="technician">Assign to Technician</Label>
            <Select
              id="technician"
              options={technicianOptions}
              value={technicianOptions.find(opt => opt.value === form.technicianId) || null}
              onChange={(opt) => setForm({
                ...form,
                technicianId: opt ? opt.value : null,
                technicianName: opt ? (opt.technicianName || opt.label) : '',
              })}
              placeholder="Select Technician..."
              isSearchable
              isDisabled={!!editData?.isFromRequest}
            />
          </FormGroup>

          <FormGroup className="mt-4">
            <Label for="remark">Remarks</Label>
            <Input
              type="textarea"
              id="remark"
              rows="3"
              value={form.remark}
              onChange={(e) => setForm({ ...form, remark: e.target.value })}
              placeholder="Enter internal remarks..."
              maxLength={22}
            />
          </FormGroup>

          <div className="mt-5 d-flex gap-2">
            <Button color="secondary" type="submit" className="flex-grow-1 py-2" disabled={isLoading}>
              {isLoading ? 'Creating TO...' : "Create TO"}
            </Button>
            <Button color="light" type="button" onClick={toggle} className="px-4">
              Cancel
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default CreateTOOffcanvas;
